// @ts-nocheck
import { Capacitor } from '@capacitor/core';
import { Camera } from '@capacitor/camera';
import { ScreenRecorder } from '@capgo/capacitor-screen-recorder';

/**
 * Ensures native OS permissions are requested before hardware access.
 */

const PERM_KEY_CAM = 'goodlyfe_perm_camera_granted';
const PERM_KEY_MIC = 'goodlyfe_perm_mic_granted';

export const requestCameraPermission = async (): Promise<boolean> => {
    console.log('[Permissions] requestCameraPermission');
    
    // Check localStorage cache first - avoids redundant checks if we already know it's granted
    if (localStorage.getItem(PERM_KEY_CAM) === 'true') {
        console.log('[Permissions] Camera permission already granted (cached)');
        return true;
    }

    if (!Capacitor.isNativePlatform()) return true;
    try {
        const status = await Camera.checkPermissions();
        console.log('[Permissions] Camera status:', status.camera);
        if (status.camera === 'granted') {
            localStorage.setItem(PERM_KEY_CAM, 'true');
            return true;
        }
        
        const result = await Camera.requestPermissions({ permissions: ['camera'] });
        console.log('[Permissions] Camera request result:', result.camera);
        if (result.camera === 'granted') {
            localStorage.setItem(PERM_KEY_CAM, 'true');
        }
        return result.camera === 'granted';
    } catch (err) {
        console.warn('[Permissions] Camera plugin failed:', err);
        return false;
    }
};

export const requestMicrophonePermission = async (): Promise<boolean> => {
    console.log('[Permissions] requestMicrophonePermission');

    // iOS WKWebView prompts for microphone access automatically when getUserMedia is called
    const isIOS = Capacitor.getPlatform() === 'ios' || (typeof navigator !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent));
    if (isIOS) return true;

    // Check localStorage cache first
    if (localStorage.getItem(PERM_KEY_MIC) === 'true') {
        console.log('[Permissions] Microphone permission already granted (cached)');
        return true;
    }

    if (!Capacitor.isNativePlatform()) return true;
    try {
        // Use @capacitor/camera which supports microphone permission requests on Android
        const status: any = await Camera.checkPermissions();
        console.log('[Permissions] Mic status (via Camera plugin):', status.microphone);
        if (status.microphone === 'granted') {
            localStorage.setItem(PERM_KEY_MIC, 'true');
            return true;
        }

        console.log('[Permissions] Requesting microphone permission via Camera plugin...');
        const result: any = await Camera.requestPermissions({ permissions: ['microphone'] } as any);
        console.log('[Permissions] Mic request result:', result.microphone);
        if (result.microphone === 'granted') {
            localStorage.setItem(PERM_KEY_MIC, 'true');
        }
        return result.microphone === 'granted';
    } catch (err) {
        console.warn('[Permissions] Microphone request failed:', err);
        return true;
    }
};

export const checkSecureContext = (): { isSecure: boolean; message: string } => {
    const isSecure = window.isSecureContext === true;
    const origin = window.location.origin;
    console.log('[Permissions] Secure Context:', isSecure, 'Origin:', origin);

    if (isSecure) {
        return { isSecure: true, message: 'Secure Context' };
    }
    
    let msg = `Insecure Context: Media access is BLOCKED on HTTP.`;
    if (origin.includes('192.168') || origin.includes('10.') || origin.includes('172.')) {
        msg += `\n\nYou are using a Network IP (${origin}). Android blocks camera/mic on non-localhost HTTP. Please use http://localhost:3000 via port forwarding.`;
    }
    
    return { isSecure: false, message: msg };
};

/**
 * Standard WebRTC audio constraints for noise cancellation, echo suppression, 
 * and clear voice capture across microphone recording components.
 */
export const VOICE_AUDIO_CONSTRAINTS: MediaTrackConstraints = {
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: true,
    channelCount: 1,
    sampleRate: 48000,
    sampleSize: 16
};

